import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../../utils/apiClient.util";
import { Api_Url } from "../../../utils/ApiUrl";

const LIMIT = 10;

const ArrowLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
  >
    <path
      d="M7.88904 2.43555L5.38763 2.43555L1.99459 8.49682L4.496 8.49682L7.88904 2.43555Z"
      fill="#869397"
    />
    <path
      d="M7.88904 14.5703L5.38763 14.5703L1.99459 8.50904L3.57108 7.46117L4.496 8.50904L7.88904 14.5703Z"
      fill="#869397"
    />
    <path
      d="M8.01655 7.35742H14.9616L13.6869 9.64313H6.74182L8.01655 7.35742Z"
      fill="#869397"
    />
  </svg>
);

const ArrowRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
  >
    <path
      d="M9.11108 2.43555L11.6125 2.43555L15.0055 8.49682L12.5041 8.49682L9.11108 2.43555Z"
      fill="#329879"
    />
    <path
      d="M9.11108 14.5703L11.6125 14.5703L15.0055 8.50904L13.429 7.46117L12.5041 8.50904L9.11108 14.5703Z"
      fill="#329879"
    />
    <path
      d="M3.31318 7.35742H10.2582L8.98351 9.64313H2.03845L3.31318 7.35742Z"
      fill="#329879"
    />
  </svg>
);

const SwapIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M9 4L11 6L9 8"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1 6H11"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const truncate = (str) => (str ? `${str.slice(0, 6)}...${str.slice(-4)}` : "-");

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const fmtTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const fmt = (n, dec = 2) =>
  n != null && n !== 0
    ? Number(n).toLocaleString("en-US", { maximumFractionDigits: dec })
    : null;

const getActionInfo = (action) => {
  if (!action) return { label: "Not yet acted", cls: "" };
  if (action === "claimed_usdt")
    return { label: "Claimed USDT", cls: "claimed" };
  if (action === "both") return { label: "Both", cls: "both" };
  if (action === "bought_kai") return { label: "Bought KAI", cls: "bought" };
  return { label: action, cls: "" };
};

const getStatusInfo = (status, action) => {
  if (!action) return { label: "Awaiting", cls: "awaiting" };
  if (status === "completed") return { label: "Completed", cls: "completed" };
  return { label: status || "Awaiting", cls: "awaiting" };
};

const getPageNumbers = (current, total) => {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, 5];
  if (current >= total - 2)
    return [total - 4, total - 3, total - 2, total - 1, total];
  return [current - 2, current - 1, current, current + 1, current + 2];
};

const buildCSV = (rows) => {
  const headers = [
    "Wallet Address",
    "Email",
    "Action",
    "Rain Burn",
    "USDT Value",
    "USDT to KAI",
    "KAI Received",
    "USDT to Wallet",
    "Status",
    "Transaction Hash",
    "Transaction Time",
  ];
  const escape = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.walletAddress || "",
        r.email || "",
        r.action || "Not yet acted",
        r.leftToClaim ?? 0,
        r.claimableUsdt ?? 0,
        r.usdtToKai ?? 0,
        r.kaiReceived ?? 0,
        r.usdtToWallet ?? 0,
        r.status || "Awaiting",
        r.transactionHash || "",
        r.transactionTime || "",
      ]
        .map(escape)
        .join(","),
    ),
  ].join("\n");
};

const downloadBlob = (content, filename) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Userslist = () => {
  const CHAIN = parseInt(process.env.REACT_APP_CHAIN_ID, 10);

  const chainNetwork =
    CHAIN == 1 ? "mainnet" : CHAIN == 11155111 ? "testnet" : null;

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchUsers = useCallback(async (page) => {
    setLoading(true);
    try {
      const params = { offset: page, limit: LIMIT };
      const res = await apiClient.get("/rain-payback/user/data", { params });
      const { data } = res.data;
      setRows(data.data || []);
      setTotalCount(data.count || 0);
      setTotalPages(data.pages || 1);
    } catch (e) {
      console.error("Failed to fetch users", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [fetchUsers, currentPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${Api_Url}/rain-payback/bulk/data`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!response.ok) throw new Error("Export failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let raw = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
      }
      raw += decoder.decode();

      const data = JSON.parse(raw);
      downloadBlob(buildCSV(data), "rain-exit-users.csv");
    } catch (e) {
      console.error("Export failed", e);
    } finally {
      setExporting(false);
    }
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);
  const startRow = (currentPage - 1) * LIMIT + 1;
  const endRow = Math.min(currentPage * LIMIT, totalCount);
  return (
    <section className="userlistmain">
      <div className="listtop">
        <div className="listheadings">
          <h1 className="listhead">Users Data List</h1>
          <p className="listpara">
            Every burn / claim / KAI purchase / Not yet acted
          </p>
        </div>
        <button
          className="exportbtn"
          onClick={handleExportCSV}
          disabled={exporting}
        >
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      <div className="listtable">
        <div className="maintable">
          <table>
            <thead>
              <tr>
                <th>Wallet</th>
                <th>Email</th>
                <th>Action</th>
                <th>Rain Burn</th>
                <th>USDT Value</th>
                <th>
                  <div className="flexmain">
                    USDT <SwapIcon /> KAI
                  </div>
                </th>
                <th>KAI Received</th>
                <th>
                  <div className="flexmain flex-end">
                    USDT <SwapIcon /> Wallet
                  </div>
                </th>
                <th>Date</th>
                <th>Status</th>
                <th>TX</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#77868B",
                    }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#77868B",
                    }}
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  const actionInfo = getActionInfo(row.action);
                  const statusInfo = getStatusInfo(row.status, row.action);
                  const usdtToKaiVal = fmt(row.usdtToKai);
                  const kaiVal = fmt(row.kaiReceived, 0);
                  const usdtToWalletVal = fmt(row.usdtToWallet);
                  return (
                    <tr key={row._id || i}>
                      <td>{truncate(row.walletAddress)}</td>
                      <td>{row.email || "-"}</td>
                      <td>
                        {actionInfo.cls ? (
                          <span className={`mainaction ${actionInfo.cls}`}>
                            {actionInfo.label}
                          </span>
                        ) : (
                          <span style={{ color: "#77868B", fontSize: 12 }}>
                            {actionInfo.label}
                          </span>
                        )}
                      </td>
                      <td>
                        {row.leftToClaim != null
                          ? fmt(row.leftToClaim, 0)
                          : "-"}
                      </td>
                      <td>
                        {row.claimableUsdt != null
                          ? `$${fmt(row.claimableUsdt)}`
                          : "-"}
                      </td>
                      <td>{usdtToKaiVal ? `$${usdtToKaiVal}` : "-"}</td>
                      <td>{kaiVal ? `${kaiVal} KAI` : "-"}</td>
                      <td className={`end${!usdtToWalletVal ? " dull" : ""}`}>
                        {usdtToWalletVal ? `$${usdtToWalletVal}` : "-"}
                      </td>
                      <td>
                        <div className="datetime">
                          {fmtDate(row.transactionTime)}
                          {fmtTime(row.transactionTime) && (
                            <span className="dull">
                              {" "}
                              {fmtTime(row.transactionTime)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={`status ${statusInfo.cls}`}>
                          <span className="circle"></span>
                          <p className="statuspara">{statusInfo.label}</p>
                        </div>
                      </td>
                      <td>
                        <div className="forflex">
                          {truncate(row.transactionHash)}{" "}
                          {row.transactionHash && (
                            <a
                              href={chainNetwork == "testnet" ? `https://sepolia.arbiscan.io/tx/${row.transactionHash}` : `https://arbiscan.io/tx/${row.transactionHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                              >
                                <path
                                  d="M5.83328 2.91665H4.78328C4.12989 2.91665 3.80295 2.91665 3.55339 3.04381C3.33386 3.15566 3.15552 3.33401 3.04366 3.55353C2.9165 3.80309 2.9165 4.13003 2.9165 4.78343V9.21676C2.9165 9.87015 2.9165 10.1967 3.04366 10.4462C3.15552 10.6658 3.33386 10.8444 3.55339 10.9563C3.8027 11.0833 4.12925 11.0833 4.78137 11.0833H9.21831C9.87042 11.0833 10.1965 11.0833 10.4458 10.9563C10.6653 10.8444 10.8443 10.6656 10.9561 10.4461C11.0832 10.1968 11.0832 9.87057 11.0832 9.21845V8.16665M8.74984 2.33331H11.6665V5.24998M11.6665 2.33331L7.58317 6.41665"
                                  stroke="#77868B"
                                  stroke-width="1.2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="footer-content newfootercontent">
          <div className="left-f">
            <h6>
              {totalCount > 0
                ? `SHOWING ${startRow}-${endRow} OF ${totalCount}`
                : "NO DATA"}
            </h6>
          </div>
          <div className="right-f">
            <nav aria-label="Page navigation">
              <ul className="pagination">
                <li className="page-item">
                  <a
                    className="page-link"
                    href="#prev"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                  >
                    <ArrowLeft />
                  </a>
                </li>
                {pageNumbers.map((page) => (
                  <li key={page} className="page-item">
                    <a
                      className={`page-link${page === currentPage ? " active" : ""}`}
                      href={`#page-${page}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page);
                      }}
                    >
                      {page}
                    </a>
                  </li>
                ))}
                <li className="page-item">
                  <a
                    className="page-link"
                    href="#next"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                  >
                    <ArrowRight />
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Userslist;
